import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, ExternalLink, Trash2, Filter, RefreshCw, TrendingUp, Layers, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { getRiskBadge, getApyColor, ITEMS_PER_PAGE } from "./helpers";
import { SortableHeader, applySorting } from "@/components/SortableHeader";
import { useState } from "react";
import { QuickDetailModal, buildDefiDetail } from "./QuickDetailModal";

export const DefiTab = ({
  opportunities, stats, minTvl, setMinTvl, tokenFilter, setTokenFilter,
  minApy, setMinApy, maxApy, setMaxApy, riskFilter, setRiskFilter,
  sortBy, setSortBy, loading, handleRefresh, saveToMyDeals, confirmDelete,
  isDemo, t,
}) => {
  const filteredOpportunities = opportunities.filter(opp => {
    if (minApy && opp.apy < parseFloat(minApy)) return false;
    if (maxApy && opp.apy > parseFloat(maxApy)) return false;
    if (riskFilter === "low" && opp.risk_score > 30) return false;
    if (riskFilter === "medium" && (opp.risk_score <= 30 || opp.risk_score > 70)) return false;
    if (riskFilter === "high" && opp.risk_score <= 70) return false;
    return true;
  });

  const [currentPage, setCurrentPage] = [stats._page, stats._setPage];
  const [colSort, setColSort] = useState(null);
  const [detailItem, setDetailItem] = useState(null);

  const sortedOpportunities = (() => {
    let items = [...filteredOpportunities];
    if (colSort) {
      return applySorting(items, colSort, {
        name: o => (o.name || "").toLowerCase(),
        protocol: o => (o.protocol || "").toLowerCase(),
        chain: o => (o.chain || "").toLowerCase(),
        token: o => (o.token || "").toLowerCase(),
        apy: o => o.apy || 0,
        tvl: o => o.tvl || 0,
        risk: o => o.risk_score || 50,
      });
    }
    return items.sort((a, b) => {
      if (sortBy === "apy") return (b.apy || 0) - (a.apy || 0);
      if (sortBy === "tvl") return (b.tvl || 0) - (a.tvl || 0);
      if (sortBy === "risk") return (a.risk_score || 50) - (b.risk_score || 50);
      return 0;
    });
  })();

  const totalPages = Math.ceil(sortedOpportunities.length / ITEMS_PER_PAGE);
  const paginatedOpportunities = sortedOpportunities.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between mb-2"><p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">{t("opps.oppsFound")}</p><Sparkles className="w-5 h-5 text-primary" /></div><p className="text-3xl font-mono font-bold">{stats.total}</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between mb-2"><p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">{t("opps.avgApy")}</p><TrendingUp className="w-5 h-5 text-green-500" /></div><p className="text-3xl font-mono font-bold text-green-500">{stats.avgApy}%</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between mb-2"><p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">{t("opps.totalTvl")}</p><Layers className="w-5 h-5 text-blue-500" /></div><p className="text-3xl font-mono font-bold">${(stats.totalTvl / 1e9).toFixed(1)}B</p></CardContent></Card>
      </div>

      <Card><CardContent className="py-4"><div className="flex flex-col lg:flex-row gap-4 items-end">
        <div className="flex-1"><Label className="text-xs">Min TVL</Label><Select value={minTvl} onValueChange={setMinTvl}><SelectTrigger data-testid="min-tvl-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="100000">$100K+</SelectItem><SelectItem value="1000000">$1M+</SelectItem><SelectItem value="10000000">$10M+</SelectItem><SelectItem value="100000000">$100M+</SelectItem></SelectContent></Select></div>
        <div className="flex-1"><Label className="text-xs">Token Filter</Label><Input data-testid="token-filter" placeholder="USDC,ETH,BTC" value={tokenFilter} onChange={(e) => setTokenFilter(e.target.value)} /></div>
        <div className="flex-1"><Label className="text-xs">Min APY (%)</Label><Input data-testid="min-apy-filter" type="number" placeholder="0" value={minApy} onChange={(e) => setMinApy(e.target.value)} /></div>
        <div className="flex-1"><Label className="text-xs">Max APY (%)</Label><Input data-testid="max-apy-filter" type="number" placeholder="1000" value={maxApy} onChange={(e) => setMaxApy(e.target.value)} /></div>
        <div className="flex-1"><Label className="text-xs">Risk Level</Label><Select value={riskFilter} onValueChange={setRiskFilter}><SelectTrigger data-testid="risk-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Risks</SelectItem><SelectItem value="low">Low Risk</SelectItem><SelectItem value="medium">Medium Risk</SelectItem><SelectItem value="high">High Risk</SelectItem></SelectContent></Select></div>
        <div className="flex-1"><Label className="text-xs">Sort By</Label><Select value={sortBy} onValueChange={setSortBy}><SelectTrigger data-testid="sort-by-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="apy">Highest APY</SelectItem><SelectItem value="tvl">Highest TVL</SelectItem><SelectItem value="risk">Lowest Risk</SelectItem></SelectContent></Select></div>
        <Button onClick={handleRefresh} disabled={loading}><Filter className="w-4 h-4 mr-2" />Apply</Button>
      </div></CardContent></Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80">{t("opps.availableOpps")}</CardTitle>
          <p className="text-sm text-muted-foreground">Page {currentPage} of {totalPages || 1} ({sortedOpportunities.length} results)</p>
        </CardHeader>
        <CardContent className="p-0">
          {paginatedOpportunities.length > 0 ? (
            <>
              <Table>
                <TableHeader><TableRow>
                  <SortableHeader label="Opportunity" sortKey="name" currentSort={colSort} onSort={setColSort} />
                  <SortableHeader label="Protocol" sortKey="protocol" currentSort={colSort} onSort={setColSort} />
                  <SortableHeader label="Chain" sortKey="chain" currentSort={colSort} onSort={setColSort} />
                  <SortableHeader label="Token" sortKey="token" currentSort={colSort} onSort={setColSort} />
                  <SortableHeader label="APY" sortKey="apy" currentSort={colSort} onSort={setColSort} className="text-right" />
                  <SortableHeader label="TVL" sortKey="tvl" currentSort={colSort} onSort={setColSort} className="text-right" />
                  <SortableHeader label="Risk" sortKey="risk" currentSort={colSort} onSort={setColSort} className="text-center" />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {paginatedOpportunities.map((opp, index) => (
                    <TableRow key={opp.opportunity_id || index} data-testid={`opp-row-${index}`} className="cursor-pointer hover:bg-secondary/40" onClick={() => setDetailItem(opp)}>
                      <TableCell><div className="flex items-center gap-2"><p className="font-medium truncate max-w-[200px]">{opp.name}</p>{opp.is_manual && <Badge variant="outline" className="text-[10px]">Manual</Badge>}</div></TableCell>
                      <TableCell>{opp.protocol}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{opp.chain}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{opp.token}</TableCell>
                      <TableCell className={`text-right font-mono font-bold ${getApyColor(opp.apy)}`}>{(opp.apy || 0).toFixed(1)}%</TableCell>
                      <TableCell className="text-right font-mono">${Math.round(opp.tvl / 1e6)}M</TableCell>
                      <TableCell className="text-center">{getRiskBadge(opp.risk_score)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {!opp.is_manual && !isDemo && (<Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => saveToMyDeals(opp, "defi")} data-testid={`opp-save-${index}`}><Tag className="w-3 h-3" /> Add to MyDeals</Button>)}
                          {opp.url && (<Button variant="default" size="sm" onClick={() => window.open(opp.url, "_blank")} className="bg-primary text-primary-foreground text-xs" data-testid={`opp-visit-${index}`}><ExternalLink className="w-3 h-3 mr-1" />Visit Vault</Button>)}
                          {opp.is_manual && (<Button variant="ghost" size="icon" onClick={() => confirmDelete(opp)}><Trash2 className="w-4 h-4 text-destructive" /></Button>)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft className="w-4 h-4 mr-1" />Previous</Button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (<Button key={pageNum} variant={currentPage === pageNum ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(pageNum)} className={currentPage === pageNum ? "bg-primary text-primary-foreground" : ""}>{pageNum}</Button>);
                    })}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">{t("opps.noOpps")}</p>
              <Button onClick={handleRefresh} disabled={loading}><RefreshCw className="w-4 h-4 mr-2" />{t("opps.refreshOpps")}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <QuickDetailModal
        isOpen={!!detailItem}
        onOpenChange={(open) => !open && setDetailItem(null)}
        item={detailItem ? buildDefiDetail(detailItem) : null}
        type="defi"
        onSave={!detailItem?.is_manual ? () => saveToMyDeals(detailItem, "defi") : null}
        isDemo={isDemo}
      />
    </>
  );
};
